import { Typography, useEventCallback } from '@mui/material';
import FilterNotificationsSide from 'civil-status/components/research-notifications-side/filter';
import { ResearchNotificationsSideCrumbs } from 'civil-status/constants/breadcrumb';
import { CriteriRicercaNotifiche } from 'civil-status/model/notifications';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import TableNotificationsSide from 'civil-status/components/research-notifications-side/table';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import {
  FilterResearchNotificationsSide,
  NotificationsListSide,
} from 'civil-status/view-models/notifications-list-side';
import { headCells } from './constants';
import { NotificationSide } from 'civil-status/model/notification-side';
import { transformNotificationsListSide } from 'civil-status/utils/transformer-notifications-list-side';
import { transformNotificationsResearchAnscOperator } from 'civil-status/utils/transform-notifications-research-ansc-operator';
import { noop } from 'shell/utils/noop';

const ResearchNotificationsSidePage = (): ReactElement => {
  const params = useParams();

  const nameCat = params.valueCat || undefined;
  const idCat = params.idCat || undefined;

  const filterTable = useFilterTable<FilterResearchNotificationsSide>();

  // filtri atto
  const [openNotifications, setOpenNotifications] = useState<boolean>(true);

  // filtri selezionati
  const [filterSelected, setFilterSelected] = useState<CriteriRicercaNotifiche | undefined>({});

  // stato per controllare la tabella
  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });

  const [notificationsList, getNotificationsList] = useHttpFetch<
    HttpResponse<NotificationSide>,
    Array<NotificationsListSide>
  >(`${__ACT_API_URL__}/dati-comunicazione/ricerca-notifiche-side`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformNotificationsListSide,
  });

  const getNotificationsListData = useEventCallback(
    (dataFilter?: CriteriRicercaNotifiche, page?: number, rowsPerPage?: number) => {
      (
        getNotificationsList({
          data: {
            ...dataFilter,
            datiPaginazione: {
              numPagina: page || 0,
              numRecordPagina: rowsPerPage || 10,
            },
          },
        }) as ResponsePromise<HttpResponse<NotificationSide>, Array<NotificationsListSide>>
      ).then(() => {
        // salvo i filtri dell'ultima ricerca
        setFilterSelected(dataFilter);
      });
    }
  );

  const handleSubmitResearchNotifications = useEventCallback(
    (values: FilterResearchNotificationsSide, page?: number, rowsPerPage?: number) => {
      // mantenere dati
      filterTable.setValues(values);

      const dataFilter: CriteriRicercaNotifiche =
        transformNotificationsResearchAnscOperator(values);

      setTableProps({
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      });

      getNotificationsListData(dataFilter);
    }
  );

  useEffect(() => {
    if (((notificationsList.data as Array<NotificationsListSide>) || []).length) {
      // se abbiamo risultato chiudere accordion dei filtri
      setOpenNotifications(false);
    }
  }, [notificationsList.data]);

  useEffect(() => {
    if (filterTable.values?.tableProps) {
      handleSubmitResearchNotifications(
        filterTable.values,
        filterTable.values.tableProps?.page,
        filterTable.values.tableProps?.rowsPerPage
      );
    }
  }, []);

  const setTablePropsFilterTable = useEventCallback((page?: number, rowsPerPage?: number) => {
    filterTable.setValues({
      ...filterTable.values,
      tableProps: {
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      },
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={ResearchNotificationsSideCrumbs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          {`Notifiche esclusivamente SIDe ${nameCat ? `- ${nameCat}` : ''}`}
        </Typography>
      </div>
      <FilterNotificationsSide
        openNotifications={openNotifications}
        setOpenNotifications={setOpenNotifications}
        handleSubmitResearchNotifications={handleSubmitResearchNotifications}
        idCat={idCat}
        initialValues={filterTable.values}
        visibleFilters={{
          idAnscNotification: false,
          stateNotification: false,
          typeContentNotification: false,
          owners: false,
          idEventNationalNotification: false,
        }}
        filterKindNotification={(items) => {
          return items.filter((item) => item.id === '1' || item.id === '2');
        }}
      />
      {notificationsList.data ? (
        ((notificationsList.data as Array<NotificationsListSide>) || [])?.length > 0 ? (
          <>
            <TableNotificationsSide
              data={notificationsList.data as Array<NotificationsListSide>}
              filterSelected={filterSelected}
              getNotificationsListData={getNotificationsListData}
              idCat={idCat}
              nameCat={nameCat}
              tableProps={tableProps}
              headCells={headCells}
              setTableProps={setTableProps}
              setTablePropsFilterTable={setTablePropsFilterTable}
              minWidth={1300}
              onConfirmMassively={noop}
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

ResearchNotificationsSidePage.displayName = 'Research NotificationsSide Page component';

export default ResearchNotificationsSidePage;
